import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Stage } from './Reveal';
import { timeline, narratedTimeline, beatAt, quadrant, captionSize, type Storyboard, type StoryCaption } from '../lib/storyboard';
import { startPlayer, type Player } from '../lib/story-player';
import { track } from '../analytics';

/**
 * The story's print (well/research/16 C1, played as soundtrack option A): the
 * crew's four-panel page of the story you told, held face-down like every
 * other print (components/Reveal.tsx) and turned over by your tap. Open, it is
 * the page with your own words lettered into it, and "Play it" reads it back
 * over a bed of music, a page at a time.
 */

/**
 * The page: the drawing, with each caption lettered into the cream band at the
 * top of its panel. `src` shows another drawing of the same page (Before, on a
 * recast); `wipeFrom` opens on that one and wipes the new page across it panel
 * by panel, the cast change (well/product/32 §2 step 5); `outline` rings the
 * teller in panel 1, the one "Want to be in it?" points at.
 */
export function StoryPage({ sb, src, wipeFrom, outline }: { sb: Storyboard; src?: string; wipeFrom?: string; outline?: boolean }) {
  const url = src ?? sb.pageUrl;
  return (
    <div className={`sb-page ${outline ? 'asking' : ''}`}>
      {(wipeFrom ?? url) && <img src={wipeFrom ?? url} alt={`Four panels: ${(sb.panels || []).map((p) => p.quote).join(' / ')}`} />}
      {wipeFrom && url && [0, 1, 2, 3].map((i) => (
        <span key={i} className={`sb-wipe sb-w${i}`} style={{ backgroundImage: `url(${url})`, backgroundPosition: quadrant(i), animationDelay: `${0.5 + i * 0.32}s` }} aria-hidden="true" />
      ))}
      {(sb.panels || []).map((p, i) => (
        <div key={i} className={`sb-cap sb-q${i} ${captionSize(p.quote)}`} aria-hidden="true">{p.quote}</div>
      ))}
      {outline && <span className="sb-outline" aria-hidden="true" />}
    </div>
  );
}

/**
 * The playback: the panels one at a time with a slow push, the caption in its
 * band. Over the narrator, each page turns on its line; with no narration (it
 * failed) it plays in captions, each lit at the speed it was said.
 */
export function StoryPlayback({ sb, player, onClose }: { sb: Storyboard; player: Player; onClose: () => void }) {
  const narrated = !!(sb.narration && player.narration);
  const tl = useMemo(() => (narrated ? narratedTimeline(sb, sb.narration!.segments, sb.narration!.seconds) : timeline(sb, false)), [sb, narrated]);
  const [t, setT] = useState(0);
  const [ended, setEnded] = useState(false);
  const started = useRef(0);
  const raf = useRef(0);
  const stopping = useRef(0);
  const still = typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  useEffect(() => {
    // a remount (React's dev double-mount) cancels the stop its unmount asked for
    window.clearTimeout(stopping.current);
    started.current = performance.now();
    const tick = () => {
      const now = narrated ? player.narration!.currentTime : (performance.now() - started.current) / 1000;
      setT(now);
      if (now >= tl.total || (narrated && player.narration!.ended)) { setEnded(true); setT(tl.total); player.stop(); return; }
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(raf.current); stopping.current = window.setTimeout(() => player.stop(), 0); };
  }, [narrated, player, tl.total]);

  const beat = beatAt(tl, t);
  const caption = (words: { text: string; at: number }[]) => <p className="sb-words">{words.map((w, k) => <span key={k} className={t >= w.at ? 'lit' : ''}>{w.text} </span>)}</p>;

  // on the body, not inside the page: a fixed layer inside an animated one is fixed to it, not to the screen
  return createPortal(
    <div className="sb-play" role="dialog" aria-label="Your story, played back">
      <button className="sb-close" onClick={onClose} aria-label="Close">×</button>
      <div className="sb-stage">
        {beat?.kind === 'panel' && (
          <div key={beat.index} className="sb-shot">
            <div className={`sb-shot-img ${still ? '' : beat.index % 2 ? 'pull' : 'push'}`} style={{ backgroundImage: `url(${sb.pageUrl})`, backgroundPosition: quadrant(beat.index), animationDuration: `${Math.max(2, beat.to - beat.from)}s` }} />
            <div className={`sb-shot-cap ${captionSize((sb.panels as StoryCaption[])[beat.index].quote)}`}>{caption(beat.words)}</div>
          </div>
        )}
        {beat?.kind === 'end' && <div key="end" className="sb-shot sb-shot-end"><div className="sb-end-words">{caption(beat.words)}</div></div>}
      </div>
      <div className="sb-controls">
        {ended
          ? <><button className="btn small butter" onClick={() => { track('storyboard_replayed'); onClose(); }}>Done</button></>
          : <span className="hint" style={{ color: 'var(--cream)' }}>{narrated ? 'Read by the Moon' : 'Your story, in your words'}</span>}
      </div>
      <div className="sb-progress"><i style={{ width: `${Math.min(100, (t / Math.max(1, tl.total)) * 100)}%` }} /></div>
    </div>,
    document.body,
  );
}

/**
 * Open: the page full screen on the dark stage, signed, with the last line on
 * its card, and Play it. `extra` sits under Play it, above the way on: the
 * send, for a story about you and a friend (components/SendStory.tsx). `ask`
 * takes the place of the way on: "Want to be in it?" (components/Cast.tsx),
 * with the teller ringed. A recast page carries a Before / After toggle, and
 * its title card says who it stars.
 */
export function StoryOpen({ sb, onDone, doneLabel = 'Keep going', extra, kicker, ask, wipeFrom, below }: { sb: Storyboard; onDone: () => void; doneLabel?: string; extra?: ReactNode; kicker?: string; ask?: ReactNode; wipeFrom?: string; /** under the actions: the recast's own buttons */ below?: ReactNode }) {
  const [player, setPlayer] = useState<Player | null>(null);
  const [before, setBefore] = useState(false);
  const play = () => { track('storyboard_played', { narrated: !!sb.narration }); setPlayer(startPlayer(sb)); };
  const toggle = (b: boolean) => { setBefore(b); if (b) track('cast_before_after', { storyId: sb.storyId }); };
  return (
    <div className="stage story-open" role="region" aria-label="Your story, storyboarded">
      <div className="story-open-inner">
        <span className="story-open-kicker">{kicker || `Storyboarded by the Moon · your Day ${sb.day} story`}</span>
        <h1 className="sb-title story-open-title">{sb.title}{sb.cast?.name && <small className="sb-starring"> · starring {sb.cast.name}</small>}</h1>
        <StoryPage sb={sb} outline={!!ask} wipeFrom={before ? undefined : wipeFrom} src={before ? sb.beforePageUrl : undefined} />
        {sb.beforePageUrl && !ask && (
          <div className="ba-toggle" role="group" aria-label="Before and after">
            <button type="button" className={before ? 'on' : ''} aria-pressed={before} onClick={() => toggle(true)}>Before</button>
            <button type="button" className={before ? '' : 'on'} aria-pressed={!before} onClick={() => toggle(false)}>With you</button>
          </div>
        )}
        {sb.ending && <div className="sb-end-card">{sb.ending.quote}</div>}
        <div className="story-open-actions">
          {ask ?? (
            <>
              <button className={extra ? 'btn butter' : 'btn'} onClick={play}>▶ Play it</button>
              {extra && <div style={{ margin: '10px 0 4px' }}>{extra}</div>}
              <button className="btn ghost story-open-next" onClick={onDone}>{doneLabel}</button>
              {below}
            </>
          )}
        </div>
      </div>
      {player && <StoryPlayback sb={sb} player={player} onClose={() => setPlayer(null)} />}
    </div>
  );
}

/** One tap turns the print over, then the page opens: the same turn every other print makes. */
export function StoryReveal({ sb, onDone, doneLabel, extra, kicker, ask, wipeFrom, below }: { sb: Storyboard; onDone: () => void; doneLabel?: string; extra?: ReactNode; kicker?: string; ask?: ReactNode; wipeFrom?: string; below?: ReactNode }) {
  const [stage, setStage] = useState<'turning' | 'open'>('turning');
  useEffect(() => {
    const img = new Image();
    if (sb.pageUrl) img.src = sb.pageUrl;
    const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const t = setTimeout(() => setStage('open'), still ? 0 : 750);
    try { navigator.vibrate?.(12); } catch { /* not a phone */ }
    return () => clearTimeout(t);
  }, [sb.pageUrl]);
  if (stage === 'turning') {
    return (
      <Stage bg="/art/reveal/wall.jpg" dim className="peg-scene" label="Turning it over">
        <div className="line" />
        <div className="hanger turning">
          <img className="peg" src="/art/reveal/peg.webp" alt="" draggable={false} />
          <div className="flip">
            <span className="print-back face" />
            <span className="print-front face"><img src={sb.pageUrl} alt="" /></span>
          </div>
        </div>
      </Stage>
    );
  }
  return <StoryOpen sb={sb} onDone={onDone} doneLabel={doneLabel} extra={extra} kicker={kicker} ask={ask} wipeFrom={wipeFrom} below={below} />;
}
